import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Heart, Utensils, Users, MapPin, Calendar, Star, 
  ChevronLeft, ChevronRight, Quote, Send, Eye, EyeOff,
  CheckCircle, Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OutreachPage() {
  const [media, setMedia] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ total: 0, average_rating: 0 });
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [reviewForm, setReviewForm] = useState({
    name: '',
    email: '',
    phone: '',
    rating: 5,
    title: '',
    content: '',
    display_anonymous: false
  });

  useEffect(() => {
    fetchMedia();
    fetchReviews();
  }, []);

  const fetchMedia = async () => {
    try {
      const response = await axios.get(`${API}/media/page/outreach`);
      setMedia(response.data.media || []);
    } catch (error) {
      console.error('Failed to fetch media:', error);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/public/outreach`);
      setReviews(response.data.reviews || []);
      setReviewStats({
        total: response.data.total || 0,
        average_rating: response.data.average_rating || 0
      });
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    }
  };

  const nextImage = () => {
    if (media.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % media.length);
    }
  };

  const prevImage = () => {
    if (media.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + media.length) % media.length);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/reviews/submit`, {
        ...reviewForm,
        page: 'outreach'
      });
      
      setSubmitted(true);
      toast.success('Thank you! Your review will be visible once approved.');
      
      // Reset form after delay
      setTimeout(() => {
        setShowReviewForm(false);
        setSubmitted(false);
        setReviewForm({
          name: '',
          email: '',
          phone: '',
          rating: 5,
          title: '',
          content: '',
          display_anonymous: false
        });
      }, 3000);
      
    } catch (error) {
      toast.error('Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const StarRating = ({ rating, interactive = false, onRate = () => {} }) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => interactive && onRate(star)}
          className={`${interactive ? 'cursor-pointer hover:scale-110' : ''} transition-transform`}
          disabled={!interactive}
        >
          <Star
            size={interactive ? 28 : 18}
            className={star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
          />
        </button>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-cream pt-20" data-testid="outreach-page">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-green-700 to-green-900 text-white py-20 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full mb-6">
              <Heart size={18} />
              <span className="text-sm font-medium">501(c)(3) Nonprofit Organization</span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-4">
              Living Fruit Outreach
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto mb-8">
              Serving our community through food assistance, outreach programs, and compassionate care. 
              Together, we're making a difference one family at a time.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href="/donate" 
                className="inline-flex items-center gap-2 bg-white text-green-800 px-6 py-3 rounded-full font-semibold hover:bg-green-50 transition-colors"
              >
                <Heart size={18} />
                Support Our Mission
              </a>
              <a 
                href="/pantry-registration" 
                className="inline-flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-green-500 transition-colors"
              >
                <Calendar size={18} />
                Register for Pantry
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b border-gold/10">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { number: '500+', label: 'Families Served Monthly' },
              { number: '10,000+', label: 'Meals Provided' },
              { number: '50+', label: 'Volunteers' },
              { number: '5', label: 'Years of Service' }
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="text-3xl md:text-4xl font-bold text-green-700">{stat.number}</div>
                <div className="text-charcoal/60 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      {media.length > 0 && (
        <section className="py-16 px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-serif text-3xl text-charcoal text-center mb-8">Our Impact in Action</h2>
            
            {/* Main Image */}
            <div className="relative rounded-2xl overflow-hidden shadow-xl mb-4">
              <motion.img
                key={currentImageIndex}
                src={media[currentImageIndex]?.url}
                alt={media[currentImageIndex]?.title || 'Outreach photo'}
                className="w-full h-[400px] md:h-[500px] object-cover"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              />
              
              {/* Navigation Arrows */}
              {media.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 rounded-full flex items-center justify-center hover:bg-white transition-colors"
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 rounded-full flex items-center justify-center hover:bg-white transition-colors"
                  >
                    <ChevronRight size={24} />
                  </button>
                </>
              )}
              
              {/* Image Caption */}
              {media[currentImageIndex]?.title && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                  <p className="text-white font-medium">{media[currentImageIndex].title}</p>
                  {media[currentImageIndex].description && (
                    <p className="text-white/70 text-sm">{media[currentImageIndex].description}</p>
                  )}
                </div>
              )}
            </div>
            
            {/* Thumbnails */}
            {media.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {media.map((item, index) => (
                  <button
                    key={item.media_id}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      index === currentImageIndex ? 'border-green-600 scale-105' : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img src={item.url} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Programs Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-serif text-3xl text-charcoal text-center mb-12">Our Programs</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: Utensils,
                title: 'Food Pantry',
                description: 'Weekly distribution of fresh produce, groceries, and essential items to families in need. Register online for convenient pickup times.',
                link: '/pantry-registration',
                linkText: 'Register Now'
              },
              {
                icon: Users,
                title: 'Community Outreach',
                description: 'Regular events bringing resources, hope, and connection to underserved neighborhoods throughout our region.',
                link: '/events',
                linkText: 'View Events'
              },
              {
                icon: MapPin,
                title: 'Mobile Pantry',
                description: 'Bringing food assistance directly to communities with limited access, reaching those who cannot come to us.',
                link: '/pantries',
                linkText: 'Find Locations'
              },
              {
                icon: Heart,
                title: 'Emergency Assistance',
                description: 'Immediate support for families facing crisis situations, including disaster relief and emergency food boxes.',
                link: '/contact',
                linkText: 'Get Help'
              }
            ].map((program, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-cream rounded-2xl p-6 border border-gold/10"
              >
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                  <program.icon size={24} className="text-green-700" />
                </div>
                <h3 className="font-serif text-xl text-charcoal mb-2">{program.title}</h3>
                <p className="text-charcoal/70 mb-4">{program.description}</p>
                <a 
                  href={program.link}
                  className="text-green-700 font-medium hover:text-green-800 inline-flex items-center gap-1"
                >
                  {program.linkText} →
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl text-charcoal mb-4">Community Stories</h2>
            {reviewStats.total > 0 && (
              <div className="flex items-center justify-center gap-2">
                <StarRating rating={Math.round(reviewStats.average_rating)} />
                <span className="text-charcoal/60">
                  {reviewStats.average_rating.toFixed(1)} from {reviewStats.total} reviews
                </span>
              </div>
            )}
          </div>

          {/* Reviews Grid */}
          {reviews.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {reviews.map((review, i) => (
                <motion.div
                  key={review.review_id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white rounded-2xl p-6 shadow-md border border-gold/10"
                >
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Quote size={18} className="text-green-700" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-charcoal">{review.display_name}</h4>
                        <StarRating rating={review.rating} />
                      </div>
                      <p className="text-xs text-charcoal/50">
                        {new Date(review.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <h5 className="font-medium text-charcoal mb-2">{review.title}</h5>
                  <p className="text-charcoal/70 text-sm">{review.content}</p>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-2xl border border-gold/10 mb-8">
              <Quote size={40} className="text-charcoal/20 mx-auto mb-4" />
              <p className="text-charcoal/60">Be the first to share your experience!</p>
            </div>
          )}

          {/* Add Review Button/Form */}
          {!showReviewForm ? (
            <div className="text-center">
              <Button
                onClick={() => setShowReviewForm(true)}
                className="bg-green-700 hover:bg-green-800 text-white"
                data-testid="write-review-btn"
              >
                <Send size={18} className="mr-2" />
                Share Your Experience
              </Button>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-gold/10"
            >
              {submitted ? (
                <div className="text-center py-8">
                  <CheckCircle size={48} className="text-green-600 mx-auto mb-4" />
                  <h3 className="font-serif text-xl text-charcoal mb-2">Thank You!</h3>
                  <p className="text-charcoal/70">Your review has been submitted and will be visible once approved.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmitReview} className="space-y-6">
                  <h3 className="font-serif text-xl text-charcoal mb-4">Share Your Experience</h3>
                  
                  {/* Rating */}
                  <div>
                    <Label className="mb-2 block">Your Rating</Label>
                    <StarRating 
                      rating={reviewForm.rating} 
                      interactive 
                      onRate={(r) => setReviewForm({ ...reviewForm, rating: r })}
                    />
                  </div>

                  {/* Name & Email */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Your Name *</Label>
                      <Input
                        value={reviewForm.name}
                        onChange={(e) => setReviewForm({ ...reviewForm, name: e.target.value })}
                        required
                        className="bg-cream"
                      />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={reviewForm.email}
                        onChange={(e) => setReviewForm({ ...reviewForm, email: e.target.value })}
                        required
                        className="bg-cream"
                      />
                    </div>
                  </div>

                  {/* Phone */}
                  <div>
                    <Label>Phone (optional)</Label>
                    <Input
                      type="tel"
                      value={reviewForm.phone}
                      onChange={(e) => setReviewForm({ ...reviewForm, phone: e.target.value })}
                      className="bg-cream"
                    />
                  </div>

                  {/* Review Title */}
                  <div>
                    <Label>Review Title *</Label>
                    <Input
                      value={reviewForm.title}
                      onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                      placeholder="Summarize your experience"
                      required
                      className="bg-cream"
                    />
                  </div>

                  {/* Review Content */}
                  <div>
                    <Label>Your Experience *</Label>
                    <Textarea
                      value={reviewForm.content}
                      onChange={(e) => setReviewForm({ ...reviewForm, content: e.target.value })}
                      placeholder="Tell us about your experience with Living Fruit Outreach..."
                      rows={4}
                      required
                      className="bg-cream"
                    />
                  </div>

                  {/* Anonymous Option */}
                  <div className="bg-cream rounded-xl p-4 border border-gold/20">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={reviewForm.display_anonymous}
                        onChange={(e) => setReviewForm({ ...reviewForm, display_anonymous: e.target.checked })}
                        className="w-5 h-5 mt-0.5 rounded border-gold/30 text-green-600 focus:ring-green-500"
                      />
                      <div>
                        <div className="flex items-center gap-2 font-medium text-charcoal">
                          {reviewForm.display_anonymous ? <EyeOff size={16} /> : <Eye size={16} />}
                          Display as Anonymous
                        </div>
                        <p className="text-xs text-charcoal/60 mt-1">
                          Your name and contact info will be saved privately but your review will show as "Anonymous" to the public.
                        </p>
                      </div>
                    </label>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowReviewForm(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 bg-green-700 hover:bg-green-800 text-white"
                    >
                      {submitting ? (
                        <>
                          <Loader2 size={18} className="mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        'Submit Review'
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </motion.div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-green-800 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-3xl mb-4">Join Our Mission</h2>
          <p className="text-white/80 mb-8">
            Whether you donate, volunteer, or spread the word, you're helping us serve more families in need.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="/donate"
              className="inline-flex items-center gap-2 bg-white text-green-800 px-6 py-3 rounded-full font-semibold hover:bg-green-50 transition-colors"
            >
              Donate Now
            </a>
            <a 
              href="/volunteer"
              className="inline-flex items-center gap-2 bg-green-700 text-white px-6 py-3 rounded-full font-semibold hover:bg-green-600 transition-colors"
            >
              Volunteer
            </a>
          </div>
          <p className="mt-8 text-sm text-white/60">
            Living Fruit Outreach is a 501(c)(3) nonprofit organization. All donations are tax-deductible.
          </p>
        </div>
      </section>
    </div>
  );
}
